'use client';

import dynamic from 'next/dynamic';

export interface AdvancedSearchClientProps {
  apiKey: string;
  indexName: string;
  showAdminFilters: boolean;
  showOnlyPublic?: boolean;
  sessionId: string;
  livreIcon: string;
  manuscritIcon: string;
  autreIcon: string;
}

/**
 * Client-only boundary for the advanced search InstantSearch panel.
 *
 * The panel's entire state lives in localStorage (keyed by `?recid`), NOT in
 * the URL. That makes it impossible to server-render the selected-facet state
 * (unlike `/recherche`, which drives SSRLabelsProvider from the URL), so any
 * SSR of the InstantSearch tree diverges from the client once localStorage
 * state is restored and MSL facet labels hydrate — producing React hydration
 * mismatches and #310 "rendered more hooks than during the previous render".
 *
 * Rendering the panel client-only (`ssr: false`) removes the server HTML for
 * the panel entirely, so there is nothing to hydrate and the whole class of
 * SSR/hydration bugs cannot occur. The page header + description stay
 * server-rendered (SEO) in page.tsx; this interactive filter form has no SEO
 * value. `/recherche` and `/oldrecherche` are untouched.
 */
const AdvancedSearchInterface = dynamic(
  () =>
    import('./AdvancedSearchInterface').then(
      (mod) => mod.AdvancedSearchInterface,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="mx-auto max-w-6xl px-3 py-8">
        <div
          data-testid="advanced-search-loading"
          className="h-[32rem] animate-pulse rounded-lg bg-gray-100"
        />
      </div>
    ),
  },
);

export function AdvancedSearchClient(props: AdvancedSearchClientProps) {
  return <AdvancedSearchInterface {...props} />;
}
